//@ts-nocheck
import "chart.js/auto";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import { SPACING } from "../../../constants/design/spacing";
import { FUNDING_RATE_PRECISION } from "../../../constants/precision/form";
import { useFunding } from "../../../hooks/api/funding/useFunding";
import { useFundingHistory } from "../../../hooks/api/funding/useFundingHistory";
import { nanosToSeconds } from "../../../utils/date";
import { SettingsContext } from "../../../contexts/SettingsContext";
import { Select } from "../../shared/Select";
import {
  ChartContainer,
  Container,
  ContentContainer,
  HeaderContainer,
  LabelValue,
  NoDataContainer,
} from "../style";
import { IFundingChartProps } from "./types";
import {
  buildFundingChartOptions,
  buildFundingHoverPlugins,
} from "./helpers";

function FundingChart({ instrument }: IFundingChartProps) {
  const { t: translate } = useTranslation("app", { keyPrefix: "FundingChart" });
  // Subscribe so the chart re-renders when the direction scheme changes.
  useContext(SettingsContext);
  const HIGH_LOAD_MODE = process.env.REACT_APP_HIGH_LOAD_MODE === "true";
  const { data: fundingRate } = useFunding(instrument);
  const { data: fd } = useFundingHistory(instrument, 50, HIGH_LOAD_MODE);

  // Use ref instead of state — avoids re-renders on hover
  const hoveredIndexRef = useRef<number | undefined>(undefined);
  const chartRef = useRef<any>(null);

  const [fundingRateFilter, setFundingRateFilter] = useState<
    "1h" | "8h" | "annualized"
  >("1h");

  const calculateAnnualizedHourlyFunding = useCallback(
    (hourlyFunding: number) => hourlyFunding * 24 * 365,
    []
  );

  // funding_history returns [instrument_name, time, funding, mark] → [time, funding]
  const fundingData = useMemo(() => {
    const d = (
      fd?.funding_history?.map((v) => [String(nanosToSeconds(v[1])), v[2]]) ||
      []
    ).reverse();
    if (fundingRate?.funding_rate) {
      d.push([String(Date.now() / 1000), fundingRate.funding_rate]);
    }
    if (fundingRateFilter === "8h") {
      return d.map((v) => [v[0], String(Number(v[1]) * 8)]);
    }
    if (fundingRateFilter === "annualized") {
      return d.map((v) => [
        v[0],
        String(calculateAnnualizedHourlyFunding(Number(v[1]))),
      ]);
    }
    return d;
  }, [
    calculateAnnualizedHourlyFunding,
    fd?.funding_history,
    fundingRate?.funding_rate,
    fundingRateFilter,
  ]);

  const currentFundingLabel = useMemo(() => {
    const hourlyFunding = Number(fundingRate?.funding_rate || 0);
    if (fundingRateFilter === "8h") {
      const eightHoursFunding = hourlyFunding * 8;
      return (
        <LabelValue>
          <span>{translate("8h_funding_rate")}</span>
          <span
            style={{
              color:
                eightHoursFunding < 0
                  ? COLORS.down.one
                  : COLORS.up.one,
            }}
          >
            {(eightHoursFunding * 100).toFixed(FUNDING_RATE_PRECISION)}%
          </span>
        </LabelValue>
      );
    }
    if (fundingRateFilter === "annualized") {
      const annualizedFunding = calculateAnnualizedHourlyFunding(hourlyFunding);
      return (
        <LabelValue>
          <span>{translate("annualized_rate")}</span>
          <span
            style={{
              color:
                hourlyFunding < 0 ? COLORS.down.one : COLORS.up.one,
            }}
          >
            {(annualizedFunding * 100).toFixed(FUNDING_RATE_PRECISION)}%
          </span>
        </LabelValue>
      );
    }
    return (
      <LabelValue>
        <span>{translate("1h_funding_rate")}</span>
        <span
          style={{
            color:
              hourlyFunding < 0 ? COLORS.down.one : COLORS.up.one,
          }}
        >
          {(hourlyFunding * 100).toFixed(FUNDING_RATE_PRECISION)}%
        </span>
      </LabelValue>
    );
  }, [
    calculateAnnualizedHourlyFunding,
    fundingRate?.funding_rate,
    fundingRateFilter,
    translate,
  ]);

  const chartData = useMemo(() => {
    const labels = fundingData.map((v) => v[0]);
    const data: number[] = fundingData.map((v) => Number(v[1]));
    return { labels, data };
  }, [fundingData]);

  const scaleRanges = useMemo(() => {
    const rates = fundingData.map((f) => Number(f[1]));
    const minY = Math.min(...rates);
    const maxY = Math.max(...rates);
    const absMax = Math.max(Math.abs(minY), Math.abs(maxY));
    const padding = absMax * 0.2 || 0.0001;
    return { minY: minY - padding, maxY: maxY + padding };
  }, [fundingData]);

  const options = useMemo(
    () => buildFundingChartOptions(scaleRanges, hoveredIndexRef),
    [scaleRanges]
  );

  const getData = useMemo(
    () => ({
      labels: chartData.labels,
      datasets: [
        {
          label: "funding",
          data: chartData.data,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 1.5,
          borderColor: COLORS.neutral.one,
          backgroundColor: "transparent",
          tension: 0,
          fill: false,
        },
      ],
    }),
    [chartData]
  );

  const plugins = useMemo(
    () => buildFundingHoverPlugins(hoveredIndexRef),
    []
  );

  return (
    <Container>
      <HeaderContainer>
        {currentFundingLabel}
        <Select
          isRound
          buttonSpacing={SPACING.one}
          buttonHighlightColor={TEXT_COLORS.one}
          options={[
            {
              label: translate("1h_rate"),
              isActive: fundingRateFilter === "1h",
              onClick: () => setFundingRateFilter("1h"),
            },
            {
              label: translate("8h_rate"),
              isActive: fundingRateFilter === "8h",
              onClick: () => setFundingRateFilter("8h"),
            },
            {
              label: translate("annualized"),
              isActive: fundingRateFilter === "annualized",
              onClick: () => setFundingRateFilter("annualized"),
            },
          ]}
        />
      </HeaderContainer>
      <div
        style={{
          fontSize: 11,
          lineHeight: 1.4,
          color: TEXT_COLORS.two,
          padding: `0 ${SPACING.two}px ${SPACING.two}px`,
        }}
      >
        {translate("funding_cadence_caption")}
      </div>
      <ContentContainer>
        {!fundingData?.length ? (
          <NoDataContainer>{translate("no_data")}</NoDataContainer>
        ) : (
          <ChartContainer>
            <Line
              ref={chartRef}
              data={getData}
              options={options}
              plugins={plugins}
            />
          </ChartContainer>
        )}
      </ContentContainer>
    </Container>
  );
}

export default FundingChart;
